import { Kostentraeger } from './kostentraeger-extraction-model';
import {
  DomainConfiguration,
  ExtractionSectionViewType,
} from '../../domain-extraction-specifications';
import { ExtractedRowForm } from '../../extraction-form-models';
import { Kopfdaten } from './kopfdaten-extraction-model';
import { Forderungen } from './forderungen-table-field-model';
import {
  createDocumentDataFieldGroup,
  FieldGroupModel,
  FieldModel,
  TableModel,
} from '../extraction-models';
import { ProofWorkflowHookService } from '../../../service/workflow-hooks/proof/proof-workflow-hook.service';
import { Daten301erExtractionModel } from './301er-daten-extraction-model';
import { Diagnosen } from './diagnosen-table-field-model';
import { OperationenUndProzeduren } from './operationen-und-prozeduren-table-field-model';
import { CtBatchSource, DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { ConsolidationSectionEingangstyp } from './consolidation-sections';
import { Sachbearbeiter } from './sachbearbeiter-extraction-model';
import {
  FragebogenSectionPage1,
  FragebogenSectionPage2,
  FragebogenSectionPage3,
  FragebogenSectionPage4,
} from '../fragebogen-model';
import { AuftragESESections } from '../ese-model';
import { AuftragAttestSections } from '../attest-model';

const createFieldGroupModel = <const T extends readonly FieldModel[]>(
  group: Omit<FieldGroupModel<T>, 'type'>,
): FieldGroupModel<T> => ({
  ...group,
  type: 'fieldGroupModel',
});

const createTableModel = <const T extends FieldModel[], Q extends T[number][]>(
  group: Omit<TableModel<T, Q>, 'type'>,
): TableModel<T, Q> => ({
  ...group,
  type: 'tableModel',
});

export const proofConfig = {
  extractionConfigurationByClass: {
    '25_Q3_FORDERUNGSAUFSTELLUNG': {
      extractionPages: [
        {
          sections: [
            createDocumentDataFieldGroup(),
            createFieldGroupModel({
              key: 'metadata_fields',
              label: 'Vorgang',
              fieldModels: [
                Kopfdaten.kopfdaten_vorname_betroffene_person,
                Kopfdaten.kopfdaten_nachname_betroffene_person,
                Kopfdaten.kopfdaten_geburtsdatum,
                Kopfdaten.kopfdaten_forderungsdatum,
                Kopfdaten.kopfdaten_schadendatum,
                Kopfdaten.kopfdaten_kostentraegerzeichen,
              ],
              index: 6,
            }),
            createFieldGroupModel({
              key: 'kostentraeger_fields',
              label: 'Kostenträger',
              fieldModels: [
                Kostentraeger.kostentraeger_name,
                Kostentraeger.kostentraeger_strasse,
                Kostentraeger.kostentraeger_hausnummer,
                Kostentraeger.kostentraeger_postleitzahl,
                Kostentraeger.kostentraeger_stadt,
              ],
              index: 8,
            }),
          ],
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: [
            createTableModel({
              key: 'forderungen',
              label: 'Forderungen',
              rowClassification: {
                classes: {
                  STATIONARY: 'Stationär',
                  AMBULANT: 'Ambulant',
                  CARE: 'Pflege',
                  PAY_RATE: 'Lohnersatz',
                  PHYSIOTHERAPY: 'Therapie',
                  TRAVEL_COSTS: 'Fahrtkosten',
                  AIDS: 'Hilfsmittel',
                  MEDICINE: 'Medikament & Verband',
                  CREDIT: 'Gutschrift',
                  OTHER: 'Diverses',
                },
                classLabel: 'Art',
                classColumnWidthUtilityClass: '3/12',
                visibleFieldModels: [
                  Forderungen.forderungen_beschreibung,
                  Forderungen.forderungen_betrag,
                ],
                initialValueProvider: (extractedRowData, fallBackValue) =>
                  ((extractedRowData['forderungen_betrag']?.value as number) ?? 0) < 0
                    ? 'CREDIT'
                    : fallBackValue,
                callbackProvider: (
                  extractedRow: ExtractedRowForm,
                  classValue: string | undefined,
                ) => {
                  extractedRow.controls.class.setValue(classValue);
                  const amount = extractedRow.controls.rowData.controls['forderungen_betrag']
                    .controls.value.value as number;
                  if (classValue === 'CREDIT' && amount < 0) return;
                  if (classValue !== 'CREDIT' && amount >= 0) return;

                  extractedRow.controls.rowData.controls[
                    'forderungen_betrag'
                  ].controls.value.setValue(amount * -1);
                },
              },
              fieldModels: [
                Forderungen.forderungen_beschreibung,
                Forderungen.forderungen_von,
                Forderungen.forderungen_bis,
                Forderungen.forderungen_betrag,
              ] as const,
              tableValidators: [],
              index: 10,
            }),
          ],
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
      ],
    },
    '25_Q3_301ER_DATEN': {
      extractionPages: [
        {
          sections: [
            createDocumentDataFieldGroup(),
            createFieldGroupModel({
              key: '301er_daten_institution',
              label: 'Institution',
              fieldModels: [Daten301erExtractionModel['301er_daten_ik_nummer']],
              index: 0,
            }),
            createFieldGroupModel({
              key: '301er_daten_geschaedigter',
              label: 'Geschädigter',
              fieldModels: [
                Daten301erExtractionModel['301er_daten_geschlecht'],
                Daten301erExtractionModel['301er_daten_vorname_geschaedigter'],
                Daten301erExtractionModel['301er_daten_nachname_geschaedigter'],
                Daten301erExtractionModel['301er_daten_geburtsdatum_geschaedigter'],
              ],
              index: 1,
            }),
            createFieldGroupModel({
              key: '301er_daten_aufnahme_entlassung',
              label: 'Aufnahme/Entlassung',
              fieldModels: [
                Daten301erExtractionModel['301er_daten_aufnahmedatum'],
                Daten301erExtractionModel['301er_daten_aufnahmeuhrzeit'],
                Daten301erExtractionModel['301er_daten_entlassungsdatum'],
                Daten301erExtractionModel['301er_daten_entlassungsuhrzeit'],
              ],
              index: 2,
            }),
            createFieldGroupModel({
              key: '301er_daten_abrechnungsdaten',
              label: 'Abrechnungsdaten',
              fieldModels: [
                Daten301erExtractionModel['301er_daten_beatmungsstunden'],
                Daten301erExtractionModel['301er_daten_tage_ohne_brechung'],
                Daten301erExtractionModel['301er_daten_drg'],
              ],
              index: 3,
            }),
            createFieldGroupModel({
              key: '301er_daten',
              label: 'KODIP',
              fieldModels: [
                Daten301erExtractionModel['301er_daten_geschlecht_consolidation'],
                Daten301erExtractionModel['301er_daten_aufnahmegrund'],
                Daten301erExtractionModel['301er_daten_entlassungsgrund'],
              ],
              index: 4,
            }),
          ],
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: [
            createTableModel({
              key: 'diagnosen',
              label: 'Diagnosen',
              rowClassification: {
                classes: {
                  HAUPTDIAGNOSE: 'Hauptdiagnose',
                  NEBENDIAGNOSE: 'Nebendiagnose',
                },
                classLabel: 'Art',
                classColumnWidthUtilityClass: '6/12',
                visibleFieldModels: [
                  Diagnosen.diagnosen_icd10_code,
                  Diagnosen.diagnosen_lokalisation,
                  Diagnosen.diagnosen_lokalisation_consolidation,
                ],
                initialValueProvider: (extractedRowData, fallBackValue, rowIndex) => {
                  return rowIndex === 0 ? 'HAUPTDIAGNOSE' : 'NEBENDIAGNOSE';
                },
                callbackProvider: (extractedRowForm, classValue) => {
                  extractedRowForm.controls.class.setValue(classValue);
                },
                tableCallbackProvider: (extractedRowForms, currentRowForm, classValue) => {
                  if (classValue === 'HAUPTDIAGNOSE') {
                    extractedRowForms.forEach((rowForm) => {
                      rowForm.controls.class.setValue('NEBENDIAGNOSE');
                    });
                    currentRowForm.controls.class.setValue('HAUPTDIAGNOSE');
                  }
                },
              },
              fieldModels: [
                Diagnosen.diagnosen_icd10_code,
                Diagnosen.diagnosen_lokalisation,
                Diagnosen.diagnosen_lokalisation_consolidation,
              ] as const,
              tableValidators: [],
              index: 0,
            }),
          ],
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: [
            createTableModel({
              key: 'operationen_und_prozeduren',
              label: 'Operationen und Prozeduren',
              fieldModels: [
                OperationenUndProzeduren.operationen_und_prozeduren_ops_code,
                OperationenUndProzeduren.operationen_und_prozeduren_lokalisation,
                OperationenUndProzeduren.operationen_und_prozeduren_lokalisation_consolidation,
                OperationenUndProzeduren.operationen_und_prozeduren_leistungsdatum,
              ] as const,
              tableValidators: [],
              index: 0,
            }),
          ],
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
      ],
    },
    '25_Q3_EUCON_FRAGEBOGEN': {
      extractionPages: [
        {
          sections: FragebogenSectionPage1,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: FragebogenSectionPage2,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: FragebogenSectionPage3,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
        {
          sections: FragebogenSectionPage4,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
      ],
    },
    '25_Q3_AUFTRAGSFORMULAR_ESE': {
      extractionPages: [
        {
          sections: AuftragESESections,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
      ],
    },
    '25_Q3_AUFTRAGSFORMULAR_ATTEST': {
      extractionPages: [
        {
          sections: AuftragAttestSections,
          viewType: ExtractionSectionViewType.SCROLLABLE,
        },
      ],
    },
  },
  consolidation: {
    consolidatedDocumentClasses: ['25_Q3_FORDERUNGSAUFSTELLUNG'] as DocumentClassKey[],
    batchClassification: {
      batchTypes: [
        { label: 'Regressprüfung', value: 'REGRESSPRUEFUNG' },
        { label: 'Prüfung/Berechnung Abfindungsangebot', value: 'ABFINDUNGSANGEBOT' },
        { label: 'Medizinische Expertise', value: 'MEDIZINISCHE_EXPERTISE' },
        { label: 'Pflegerische Expertise', value: 'PFLEGERISCHE_EXPERTISE' },
        { label: 'Berechnung Reserve', value: 'RESERVE' },
        { label: 'GM-Aktenlage', value: 'GUTACHTEN_AKTENLAGE' },
        { label: 'GM-Vorstellung', value: 'GUTACHTEN_VORSTELLUNG' },
        { label: 'GM-Stellungnahme', value: 'GUTACHTEN_STELLUNGNAHME' },
        { label: 'ESE', value: 'ESE' },
        { label: 'ATTEST', value: 'ATTEST' },
        { label: 'Andere Anfrage', value: 'ANDERE_ANFRAGE' },
      ],
      batchTypeKey: 'pruefungsart',
    },
    sections: [
      createFieldGroupModel({
        index: 0,
        key: 'consolidation_auftrag',
        label: 'Auftrag',
        fieldModels: [
          ConsolidationSectionEingangstyp.mandant,
          ConsolidationSectionEingangstyp.auftragsart,
          ConsolidationSectionEingangstyp.eingangstyp,
          Kopfdaten.kopfdaten_schadennummer,
          Kopfdaten.kopfdaten_kennung,
          Kopfdaten.kopfdaten_vyda_nachricht,
        ],
      }),
      createFieldGroupModel({
        index: 1,
        key: 'clerk_fields',
        label: 'Sachbearbeiter',
        visibleForSource: [CtBatchSource.NEW_BACKEND],
        fieldModels: [
          Sachbearbeiter.vorname,
          Sachbearbeiter.nachname,
          Sachbearbeiter.email,
          Sachbearbeiter.telefon,
        ],
      }),
    ],
  },
  batchSplitConfiguration: {
    splitInducingDocumentClass: '25_Q3_FORDERUNGSAUFSTELLUNG',
    splitScopedFieldKeys: [
      Kopfdaten.kopfdaten_forderungsdatum.key,
      Kopfdaten.kopfdaten_gesamtbetrag.key,
    ],
    splitScopedTableKeys: ['forderungen'] as string[],
  },
  workflowHookService: ProofWorkflowHookService,
  extractionDocumentClassesReceivedAfterSubmission: ['25_Q3_301ER_DATEN'] as DocumentClassKey[],
} as const satisfies DomainConfiguration;
